import React from 'react';
import classNames from 'classnames';
import StatusComponentV2 from './StatusComponentV2';
import SideDetailPanel from '../detection/SideDetailPanel';
import {
	BDSProvider,
	Box,
	Grid,
	Tabs,
	LocalizationProvider,
	DataTable,
	AdapterLuxon,
} from '@barracuda-internal/bds-core-v2';
import { DateTime } from 'luxon';
import { DATE_SHORTCUTS } from './constants';
import { SearchEndAdornment } from './SearchEndAdornment';
import { TopBarActions } from './TopBarActions';
import RemediatePopup from './RemediatePopup';
import ToastComponent from 'src/components/toast/ToastComponent';
import { useDetectionsDataTableLogic } from './useDetectionsDataTableLogic';
import DetectionMetricsSummary from './DetectionMetricsSummary';

const Main: React.FC = () => {
	const {
		// State
		auth,
		tab,
		statusData,
		loading,
		detectionState,
		expandPanel,
		setExpandPanel,
		selectedFeed,
		setSelectedFeed,
		showShimmer,
		setShowShimmer,
		rowSelection,
		setRowSelection,
		showDismiss,
		remediateOption,
		toasts,
		searchValue,
		searchBarRef,
		onSearchBarClickRef,
		isColumnVisibilityDrawerOpen,
		setIsColumnVisibilityDrawerOpen,
		setSideMenuSearchValue,
		datePickerError,
		datePickerErrorMessage,
		sorting,
		columnVisibility,
		categoryResponse,

		// Memoized
		tabsArray,
		columns,
		dateRange,
		selectedFeedIds,
		classifierCategories,
		sideMenuDropdowns,
		customClasses,
		classes,

		// Handlers
		handleChange,
		handleDateRangeChange,
		handleSearchFieldChange,
		handleClearSearch,
		onApplyFilters,
		handlePaginationChange,
		handleOnClickItemsPerPageOption,
		handleRowClick,
		handleRemediate,
		handleRemediateClose,
		downloadFile,
		handleRefresh,
		handlePanelSearch,
		handleSideMenuCheckboxChange,
		refreshEndDate,
		switchTab,
		setSorting,
	} = useDetectionsDataTableLogic();

	return (
		<BDSProvider>
			<div style={{ marginLeft: '24px' }}>
				{auth.isFullAdmin() && (
					<div
						className={classNames(
							expandPanel
								? classes['d_none']
								: classes['d_block'],
						)}
					>
						<Box
							sx={{
								display: 'flex',
								alignItems: 'center',
								flexDirection: 'row',
								gap: '16px',
								width: '100%',
							}}
						>
							<DetectionMetricsSummary
								dateRangeStart={detectionState.dateRangeStart}
								dateRangeEnd={detectionState.dateRangeEnd}
							/>
							<StatusComponentV2
								statusData={statusData}
								loading={loading}
								setTab={switchTab}
							/>
						</Box>
					</div>
				)}

				<div
					className={classNames(
						expandPanel ? classes['d_none'] : classes['d_block'],
					)}
				>
					<Box sx={{ marginTop: '16px' }}>
						<Tabs
							value={tab}
							onChange={handleChange}
							aria-label="detections tab panel"
							tabs={tabsArray}
							orientation={'horizontal'}
							sx={{
								minHeight: '38px',
								'& span': {
									display: 'none',
								},
								'& button': {
									minWidth: '180px !important',
								},
							}}
						/>
					</Box>

					<Box
						className={
							auth.isFullAdmin()
								? customClasses.tableContainer
								: customClasses.tableContainerNoStatus
						}
					>
						<Grid
							container
							sx={{ width: '100%', overflowX: 'auto' }}
						>
							<LocalizationProvider dateAdapter={AdapterLuxon}>
								<DataTable
									key={tab}
									layoutMode="semantic"
									columns={columns}
									data={detectionState?.tableData || []}
									dateRangeProps={{
										label: 'Date Range',
										value: dateRange,
										onChange: handleDateRangeChange,
										shortcutsItems: DATE_SHORTCUTS,
										maxDate: DateTime.local(),
										minDate: DateTime.local().minus({
											days: 90,
										}),
										error: datePickerError,
										errorMessage: datePickerErrorMessage,
									}}
									searchFieldProps={{
										value: searchValue,
										placeholder:
											'Click to search by keyword or apply filters',
										autoComplete: 'off',
										inputRef: searchBarRef,
										inputProps: {
											onClick: () =>
												onSearchBarClickRef.current?.(),
											style: { cursor: 'pointer' },
										},
										InputProps: {
											readOnly: true,
											sx: { cursor: 'pointer' },
											endAdornment: (
												<SearchEndAdornment
													searchValue={searchValue}
													onClear={handleClearSearch}
													currentSearchField={
														detectionState.searchField
													}
													currentSearchText={
														detectionState.searchText
													}
													currentFilters={
														detectionState.filters
													}
													onApplyFilters={
														onApplyFilters
													}
													classifierCategories={
														classifierCategories
													}
													searchBarRef={searchBarRef}
													onSearchBarClickRef={
														onSearchBarClickRef
													}
												/>
											),
										},
									}}
									state={{
										isLoading: detectionState.loading,
										columnVisibility,
										sorting,
										rowSelection,
									}}
									enableRowSelection={tab === 0}
									onRowSelectionChange={setRowSelection}
									// eslint-disable-next-line @typescript-eslint/no-explicit-any
									getRowId={(row) => (row as any).feedId}
									initialState={{
										expanded: true,
									}}
									isColumnVisibilityDrawerOpen={
										isColumnVisibilityDrawerOpen
									}
									onCloseColumnVisibilityDrawer={() => {
										setIsColumnVisibilityDrawerOpen(false);
										setSideMenuSearchValue('');
									}}
									onClickColumnVisibilityButton={() =>
										setIsColumnVisibilityDrawerOpen(true)
									}
									enableTopToolbar={false}
									topBarActionItems={[
										<TopBarActions
											key="top-bar-actions"
											isLoading={detectionState.loading}
											onDownload={downloadFile}
											onRefresh={handleRefresh}
											tab={tab}
											selectedCount={
												selectedFeedIds.length
											}
											remediateDisabled={
												selectedFeedIds.length === 0 &&
												selectedFeed == null
											}
											onRemediate={handleRemediate}
										/>,
									]}
									enableFilters={false}
									enableColumnFilters={false}
									enableGrouping={false}
									enableColumnDragging={false}
									manualSorting={true}
									onSortingChange={setSorting}
									enablePagination={false}
									enableStickyHeader={true}
									muiTableBodyRowProps={({ row }) => ({
										onClick: () =>
											handleRowClick(row.original),
										sx: {
											cursor:
												tab === 0
													? 'pointer'
													: 'default',
										},
									})}
									muiTableBodyCellProps={{
										sx: {
											padding: '12px',
										},
									}}
									muiTableHeadCellProps={{
										sx: {
											padding: '12px !important',
										},
									}}
									muiTableContainerProps={{
										sx: {
											marginTop: '20px',
											overflowY: 'auto',
											maxHeight: '450px',
										},
									}}
									paginationProps={{
										page: detectionState.page,
										totalItems: Math.min(
											10000,
											detectionState.totalCount,
										),
										itemsPerPage: detectionState.pageSize,
										itemsPerPageOptions: [10, 25, 50, 100],
										itemsPerPageDescription:
											'items per page',
										onChange: handlePaginationChange,
										onClickItemsPerPageOption:
											handleOnClickItemsPerPageOption,
									}}
									enableBottomToolbar={
										detectionState.totalCount > 0
									}
									sideMenuProps={{
										dropdowns: sideMenuDropdowns,
										onChange: (e) =>
											setSideMenuSearchValue(
												e.target.value.trim(),
											),
										renderDropdownItemsEmptyState: ():
											| React.ReactNode
											| React.ReactNode[] => <div />,
										subtitle: 'Select columns to add',
										title: 'Table columns',
										scrollableItemContainer: true,
									}}
									sideMenuOnCheckboxChange={
										handleSideMenuCheckboxChange
									}
									columnButtonBeforeActions={true}
								/>
							</LocalizationProvider>
						</Grid>
					</Box>
				</div>
				{selectedFeed != null && (
					<SideDetailPanel
						expand={expandPanel}
						onExpand={(e) => setExpandPanel(e)}
						esFeed={selectedFeed}
						onClose={() => {
							setSelectedFeed(null);
							setShowShimmer(true);
						}}
						setSearch={handlePanelSearch}
						allCategories={categoryResponse}
						showShimmer={showShimmer}
						setShowShimmer={setShowShimmer}
						refreshEndDate={refreshEndDate}
					/>
				)}
			</div>
			<RemediatePopup
				open={showDismiss}
				selectedFeeds={
					selectedFeedIds.length > 0
						? selectedFeedIds
						: selectedFeed != null
						? [selectedFeed.feedId]
						: []
				}
				remediateOption={remediateOption}
				onClose={handleRemediateClose}
			/>
			<ToastComponent toasts={toasts} position="bottom-left" />
		</BDSProvider>
	);
};

export default Main;
